/**
 * parseUserTopic.ts
 * 
 * Lightweight client-side topic parser.
 * Infers subject, exam context, and difficulty from free-text topic input.
 * No API call — pure keyword matching with confidence scoring.
 */

export interface ParsedTopic {
  raw: string;
  normalized: string;
  subject: string | null;        // e.g. "Biology", "Economics"
  subjectGroup: string | null;   // e.g. "Science", "Humanities"
  examContext: string | null;    // e.g. "WAEC", "BECE", "IGCSE", "General"
  difficulty: "beginner" | "intermediate" | "advanced" | null;
  confidence: "high" | "medium" | "low";
  isVague: boolean;              // true if we couldn't infer much ("Chapter 3", "Notes")
}

// ─── Subject keyword map ──────────────────────────────────────────────────────

const SUBJECT_MAP: Array<{ keywords: string[]; subject: string; group: string }> = [
  // Science
  { keywords: ["biology", "bio", "photosynthesis", "cell", "dna", "genetics", "ecology", "evolution", "organism", "anatomy", "physiology", "mitosis", "meiosis", "enzyme"], subject: "Biology", group: "Science" },
  { keywords: ["chemistry", "chem", "atom", "molecule", "reaction", "periodic", "element", "compound", "acid", "base", "titration", "bonding", "electrolysis", "organic", "inorganic"], subject: "Chemistry", group: "Science" },
  { keywords: ["physics", "phys", "force", "motion", "energy", "wave", "electric", "magnetic", "gravity", "velocity", "acceleration", "momentum", "quantum", "thermodynamics", "optics", "newton"], subject: "Physics", group: "Science" },
  { keywords: ["mathematics", "maths", "math", "algebra", "geometry", "calculus", "trigonometry", "statistics", "probability", "quadratic", "logarithm", "differentiation", "integration", "theorem", "proof"], subject: "Mathematics", group: "STEM" },
  { keywords: ["further math", "additional math", "add maths", "elective math", "elective maths"], subject: "Elective Mathematics", group: "STEM" },
  { keywords: ["integrated science", "integrated sci"], subject: "Integrated Science", group: "Science" },

  // Humanities
  { keywords: ["history", "historical", "world war", "wwi", "wwii", "ww1", "ww2", "revolution", "colonialism", "empire", "slavery", "independence", "civil war", "cold war", "apartheid"], subject: "History", group: "Humanities" },
  { keywords: ["geography", "geo", "climate", "population", "urbanisation", "urbanization", "map", "continent", "weather", "rainfall", "erosion", "volcano", "earthquake", "biome"], subject: "Geography", group: "Humanities" },
  { keywords: ["literature", "english lit", "poetry", "novel", "prose", "drama", "shakespeare", "achebe", "things fall apart", "narrative", "figurative", "metaphor", "simile", "theme"], subject: "Literature in English", group: "Humanities" },
  { keywords: ["government", "civic", "civics", "democracy", "constitution", "parliament", "judiciary", "executive", "legislature", "political", "election", "sovereignty", "federalism"], subject: "Government", group: "Humanities" },
  { keywords: ["economics", "econ", "demand", "supply", "gdp", "inflation", "market", "monopoly", "oligopoly", "microeconomics", "macroeconomics", "trade", "fiscal", "monetary", "elasticity", "opportunity cost"], subject: "Economics", group: "Humanities" },
  { keywords: ["social studies", "social science"], subject: "Social Studies", group: "Humanities" },
  { keywords: ["religious", "religion", "christian", "islam", "muslim", "bible", "quran", "theology", "crrs", "islamic studies"], subject: "Religious Studies", group: "Humanities" },

  // Languages
  { keywords: ["english", "comprehension", "grammar", "essay", "language", "vocabulary", "tense", "clause", "sentence", "paragraph", "writing", "reading"], subject: "English Language", group: "Languages" },
  { keywords: ["french", "français", "francais", "verb conjugation"], subject: "French", group: "Languages" },
  { keywords: ["twi", "ga", "hausa", "ewe", "dagbani", "ghanaian language"], subject: "Ghanaian Language", group: "Languages" },

  // Business / Commerce
  { keywords: ["accounting", "accounts", "bookkeeping", "ledger", "balance sheet", "profit and loss", "cash flow", "depreciation", "trial balance", "double entry"], subject: "Accounting", group: "Business" },
  { keywords: ["business", "management", "marketing", "entrepreneurship", "biz", "commerce", "costc accounting", "management accounting"], subject: "Business Management", group: "Business" },
  { keywords: ["cost accounting", "cost acc", "costing"], subject: "Cost Accounting", group: "Business" },

  // ICT / Computing
  { keywords: ["ict", "computer", "programming", "coding", "algorithm", "python", "java", "javascript", "html", "css", "database", "sql", "network", "cybersecurity", "ai", "machine learning", "software"], subject: "ICT / Computer Science", group: "Technology" },

  // Technical / Vocational
  { keywords: ["technical drawing", "technical draw", "engineering drawing"], subject: "Technical Drawing", group: "Technical" },
  { keywords: ["food", "nutrition", "cooking", "home economics", "home econ"], subject: "Home Economics", group: "Vocational" },
  { keywords: ["visual arts", "art", "drawing", "painting", "sculpture", "ceramics", "graphic design"], subject: "Visual Arts", group: "Arts" },
  { keywords: ["music", "theory of music", "rhythm", "melody", "harmony", "instrument"], subject: "Music", group: "Arts" },
];

// ─── Exam context map ─────────────────────────────────────────────────────────

const EXAM_MAP: Array<{ keywords: string[]; exam: string }> = [
  { keywords: ["waec", "wassce", "weac", "wace"], exam: "WASSCE" },
  { keywords: ["bece", "jhs exam", "junior high exam", "basic education certificate"], exam: "BECE" },
  { keywords: ["novdec", "nov/dec"], exam: "NOVDEC" },
  { keywords: ["igcse", "cambridge", "o level", "a level", "a-level"], exam: "IGCSE" },
  { keywords: ["jamb", "utme", "post-utme"], exam: "JAMB" },
  { keywords: ["neco"], exam: "NECO" },
  { keywords: ["sat", "act", "ap exam", "ap calculus", "ap biology", "ap chemistry", "ap english", "ap history"], exam: "SAT/ACT/AP" },
  { keywords: ["ib diploma", "international baccalaureate"], exam: "IB" },
  { keywords: ["gcse"], exam: "GCSE" },
];

// ─── Difficulty signals ───────────────────────────────────────────────────────

const DIFFICULTY_SIGNALS: Record<string, ParsedTopic["difficulty"]> = {
  "introduction to": "beginner",
  "basics of": "beginner",
  "beginner": "beginner",
  "intro ": "beginner",
  "101": "beginner",
  "advanced": "advanced",
  "elective": "advanced",
  "higher level": "advanced",
  "hl ": "advanced",
  "postgrad": "advanced",
  "university level": "advanced",
  "level 300": "advanced",
  "level 400": "advanced",
};

// ─── Vague topic detection ────────────────────────────────────────────────────

const VAGUE_PATTERNS = [
  /^chapter\s*\d+$/i,
  /^unit\s*\d+$/i,
  /^topic\s*\d*$/i,
  /^notes?$/i,
  /^revision$/i,
  /^study$/i,
  /^test$/i,
  /^exam$/i,
  /^homework$/i,
  /^assignment$/i,
  /^lesson\s*\d*$/i,
];

// ─── Main parser ──────────────────────────────────────────────────────────────

export function parseUserTopic(raw: string): ParsedTopic {
  const normalized = raw.trim().toLowerCase();

  // Check for vague input
  const isVague = VAGUE_PATTERNS.some((p) => p.test(normalized));
  if (isVague) {
    return {
      raw,
      normalized,
      subject: null,
      subjectGroup: null,
      examContext: null,
      difficulty: null,
      confidence: "low",
      isVague: true,
    };
  }

  // Match exam context
  let examContext: string | null = null;
  for (const entry of EXAM_MAP) {
    if (entry.keywords.some((kw) => normalized.includes(kw))) {
      examContext = entry.exam;
      break;
    }
  }

  // Match subject — score each match by keyword length (longer = more specific)
  let bestSubject: string | null = null;
  let bestGroup: string | null = null;
  let bestScore = 0;

  for (const entry of SUBJECT_MAP) {
    for (const kw of entry.keywords) {
      if (normalized.includes(kw) && kw.length > bestScore) {
        bestScore = kw.length;
        bestSubject = entry.subject;
        bestGroup = entry.group;
      }
    }
  }

  // Match difficulty
  let difficulty: ParsedTopic["difficulty"] = null;
  for (const [signal, level] of Object.entries(DIFFICULTY_SIGNALS)) {
    if (normalized.includes(signal)) {
      difficulty = level;
      break;
    }
  }

  // Determine confidence
  let confidence: ParsedTopic["confidence"] = "low";
  if (bestSubject && examContext) confidence = "high";
  else if (bestSubject) confidence = "medium";
  else if (examContext) confidence = "low";

  return {
    raw,
    normalized,
    subject: bestSubject,
    subjectGroup: bestGroup,
    examContext,
    difficulty,
    confidence,
    isVague: false,
  };
}

/**
 * Returns a human-readable label for what we inferred.
 * Used in the confirmation prompt copy.
 */
export function describeInference(parsed: ParsedTopic): string {
  if (parsed.subject && parsed.examContext) {
    return `${parsed.subject} (${parsed.examContext})`;
  }
  if (parsed.subject) return parsed.subject;
  if (parsed.examContext) return parsed.examContext;
  return parsed.raw;
}
